import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { getGroupHierarchy } from '@/lib/firebase/queries/hierarchical-groups';
import { addMemberOrOfferMove } from '@/lib/groups/addMemberOrOfferMove';
import { getOrganizationManagers } from '@/lib/firebase/queries/organizations/managers';
import type { Group } from '@/types/support';
import type { User } from '@/types';

interface AddToSectionDialogProps {
  customer: User | null;
  organizationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

interface SectionWithGroups {
  section: Group;
  groups: Group[];
}

export function AddToSectionDialog({ 
  customer, 
  organizationId, 
  open, 
  onOpenChange, 
  onSuccess 
}: AddToSectionDialogProps) {
  const [sections, setSections] = useState<SectionWithGroups[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<string>('');
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const { t, dir } = useLanguage();
  const isRTL = dir === 'rtl';

  useEffect(() => {
    if (open && organizationId) {
      loadSections();
    }
  }, [open, organizationId]);

  const loadSections = async () => {
    try {
      setLoading(true);
      const hierarchy = await getGroupHierarchy(organizationId);
      
      // Extract sections (parent groups) and their subgroups
      const sectionsWithGroups: SectionWithGroups[] = hierarchy
        .filter(group => group.groupType === 'parent')
        .map(section => ({
          section,
          groups: section.children || []
        }));
      
      setSections(sectionsWithGroups);
    } catch (error) {
      console.error('Error loading sections:', error);
      toast.error(t('dashboard.manager.dashboard.customers.sections.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSectionChange = (sectionId: string) => {
    setSelectedSectionId(sectionId);
    setSelectedGroupId(''); // Reset group selection when section changes
  };

  const handleAddToSection = async () => {
    if (!customer || !selectedSectionId || !selectedGroupId) {
      toast.error(t('dashboard.manager.dashboard.customers.sections.selectGroupError'));
      return;
    }

    try {
      setIsAdding(true);
      
      // Get organization managers to use one as the addedByUserId
      const managers = await getOrganizationManagers(organizationId);
      const addedByUserId = managers.length > 0 ? managers[0].id : null;
      
      if (!addedByUserId) {
        toast.error(t('dashboard.manager.dashboard.customers.sections.noManagerError'));
        return;
      }

      // Add customer to the selected group. namat#30: one group per
      // organization — if they already have one here, offer to move them.
      const groupName =
        sections.flatMap((s) => s.groups).find((g) => g.id === selectedGroupId)?.name || '';
      const outcome = await addMemberOrOfferMove({
        groupId: selectedGroupId,
        groupName,
        userId: customer.id,
        byUserId: addedByUserId,
        rtl: isRTL,
      });
      if (outcome.kind === 'declined') return;
      if (outcome.kind === 'blocked') {
        toast.error(outcome.message);
        return;
      }
      if (outcome.kind === 'failed') {
        toast.error(t('dashboard.manager.dashboard.customers.sections.addError'));
        return;
      }

      toast.success(t('dashboard.manager.dashboard.customers.sections.addSuccess', { name: customer.firstNameEn }));
      
      // Reset form
      setSelectedSectionId('');
      setSelectedGroupId('');
      
      // Close dialog and trigger success callback
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Error adding customer to section:', error);
      toast.error(t('dashboard.manager.dashboard.customers.sections.addError'));
    } finally {
      setIsAdding(false);
    }
  };

  const selectedSection = sections.find(s => s.section.id === selectedSectionId);
  const availableGroups = selectedSection?.groups || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" dir={dir}>
                 <DialogHeader className={isRTL ? 'text-right' : 'text-left'}>
           <DialogTitle className={isRTL ? 'text-right' : 'text-left'}>
             {t('dashboard.manager.dashboard.customers.sections.addToSectionTitle')}: {customer?.firstNameEn} {customer?.lastNameEn}
           </DialogTitle>
         </DialogHeader>
        
        <div className="space-y-4">
          {/* Section Selection */}
          <div className="space-y-2">
            <label className={`text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('dashboard.manager.dashboard.customers.sections.selectSection')}
            </label>
            <Select value={selectedSectionId} onValueChange={handleSectionChange}>
              <SelectTrigger className={isRTL ? 'text-right' : 'text-left'}>
                <SelectValue placeholder={t('dashboard.manager.dashboard.customers.sections.selectSectionPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {loading ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                ) : sections.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground">
                    {t('dashboard.manager.dashboard.customers.sections.noSections')}
                  </div>
                ) : (
                  sections.map((sectionData) => (
                    <SelectItem key={sectionData.section.id} value={sectionData.section.id}>
                      {isRTL ? sectionData.section.name : sectionData.section.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Group Selection */}
          {selectedSectionId && (
            <div className="space-y-2">
              <label className={`text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('dashboard.manager.dashboard.customers.sections.selectGroup')}
              </label>
              <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                <SelectTrigger className={isRTL ? 'text-right' : 'text-left'}>
                  <SelectValue placeholder={t('dashboard.manager.dashboard.customers.sections.selectGroupPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {availableGroups.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      {t('dashboard.manager.dashboard.customers.sections.noGroups')}
                    </div>
                  ) : (
                    availableGroups.map((group) => (
                      <SelectItem key={group.id} value={group.id}>
                        {isRTL ? group.name : group.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'} gap-2 pt-4`}>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleAddToSection}
              disabled={isAdding || !selectedSectionId || !selectedGroupId}
              className="bg-[#7dda55] hover:bg-[#6dc447] text-white"
            >
              {isAdding ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {t('dashboard.manager.dashboard.customers.sections.adding')}
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('dashboard.manager.dashboard.customers.sections.addToSection')}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
} 